import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import { MONTHS } from "@/lib/constants";
import FYSelect from "@/components/FYSelect";
import { loadFinancialYears, fyLabel } from "@/lib/fy";
import { Plus, X, FileDown, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Months in FY order (Apr → Mar). Each tuple is [monthIndex 1-12, offsetYear 0|1].
const FY_MONTHS = [
  [4, 0], [5, 0], [6, 0], [7, 0], [8, 0], [9, 0],
  [10, 0], [11, 0], [12, 0], [1, 1], [2, 1], [3, 1],
];

export default function TrainingCalendar() {
  const { user } = useAuth();
  const canEdit = ["hr", "super_admin"].includes(user.system_role);
  const [fy, setFy] = useState(null); // fy_start_year
  const [entries, setEntries] = useState([]);
  const [skills, setSkills] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ skill_code: "", target_month: 4, source: "TNI_driven" });
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadFinancialYears().then((d) => setFy((prev) => prev ?? d.current_fy));
  }, []);

  const load = useCallback(() => {
    if (fy === null) return Promise.resolve();
    return api.get("/calendar", { params: { fy } }).then((r) => setEntries(r.data));
  }, [fy]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/skills").then((r) => setSkills(r.data)); }, []);

  // Given a target_month (1-12), the calendar year within this FY.
  const yearForMonth = (m) => (m >= 4 ? fy : fy + 1);

  const submitCreate = async (payload) => {
    try {
      await api.post("/calendar", payload);
      toast.success("Calendar entry added");
      setShowAdd(false);
      setDuplicateWarning(null);
      setForm({ skill_code: "", target_month: 1, source: "TNI_driven" });
      load();
    } catch (err) {
      const data = err.response?.data?.detail;
      if (err.response?.status === 409 && data?.code === "DUPLICATE_CALENDAR_ENTRY") {
        setDuplicateWarning({ existing: data.existing, payload });
      } else {
        toast.error(typeof data === "string" ? data : "Failed to add entry");
      }
    }
  };

  const add = (e) => {
    e.preventDefault();
    const target_month = parseInt(form.target_month);
    const payload = { ...form, target_month, year: yearForMonth(target_month) };
    submitCreate(payload);
  };

  const confirmDuplicateCreate = async () => {
    if (!duplicateWarning) return;
    setCreating(true);
    await submitCreate({ ...duplicateWarning.payload, override_duplicate: true });
    setCreating(false);
  };

  const exportPdf = async (workforceType = null) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ fy: String(fy) });
      if (workforceType) params.set("workforce_type", workforceType);
      const res = await fetch(`${API}/calendar/pdf?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const suffix = workforceType === "worker" ? "Operators_" : workforceType === "staff" ? "Staff_" : "";
      const filename = m ? m[1] : `TrainingPlan_${suffix}FY${fy}_${new Date().toISOString().slice(0, 10)}.pdf`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully");
    } catch (e) {
      toast.error("Export failed — please try again");
    } finally {
      setExporting(false);
    }
  };

  const byMonth = MONTHS.map((m, i) => entries.filter((e) => e.target_month === i + 1));

  // Render months in FY order (Apr, May, …, Mar).
  const monthsInFYOrder = FY_MONTHS.map(([m]) => ({ index: m - 1, label: MONTHS[m - 1] }));

  return (
    <div data-testid="calendar-page">
      <PageHeader
        eyebrow="Annual Training Plan"
        title={fy ? `${fyLabel(fy)} Calendar` : "Calendar"}
        subtitle="The formal IATF training plan — one entry per competency per target month."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Calendar", to: "/calendar" }, { label: fy ? fyLabel(fy) : "" }]}
        action={
          <div className="flex items-center gap-2">
            <FYSelect value={fy} onChange={setFy} testid="calendar-fy-select" label="" />
            {canEdit && (
              <button
                onClick={() => exportPdf("staff")}
                disabled={exporting}
                data-testid="export-training-plan-staff-pdf"
                className="inline-flex items-center gap-1.5 border border-[#0D0D0D] text-[#0D0D0D] hover:bg-[#0D0D0D] hover:text-white text-sm rounded-sm px-3 py-2 disabled:opacity-50"
              >
                {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
                Staff Training Plan
              </button>
            )}
            {canEdit && (
              <button
                onClick={() => exportPdf("worker")}
                disabled={exporting}
                data-testid="export-training-plan-operators-pdf"
                className="inline-flex items-center gap-1.5 border border-[#0D0D0D] text-[#0D0D0D] hover:bg-[#0D0D0D] hover:text-white text-sm rounded-sm px-3 py-2 disabled:opacity-50"
              >
                {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
                Operator Training Plan
              </button>
            )}
            {canEdit && (
              <button
                onClick={() => exportPdf(null)}
                disabled={exporting}
                data-testid="export-training-plan-pdf"
                className="inline-flex items-center gap-1.5 bg-[#0D0D0D] hover:bg-neutral-800 text-white text-sm rounded-sm px-3 py-2 disabled:opacity-50"
              >
                {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
                {exporting ? "Generating…" : "Full Annual Plan"}
              </button>
            )}
            {canEdit && (
              <button onClick={() => setShowAdd(true)} data-testid="add-calendar-entry" className="inline-flex items-center gap-1.5 bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm px-3 py-2">
                <Plus size={14} /> Add entry
              </button>
            )}
          </div>
        }
      />

      <div className="px-4 md:px-10 py-8 space-y-8">
        {/* 12-month grid — FY order (Apr → Mar) */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {monthsInFYOrder.map(({ index, label }) => (
            <div key={label} className="bc-card p-3 min-h-[120px]" data-testid={`month-${label}`}>
              <div className="bc-label mb-2">{label}</div>
              <div className="space-y-1.5">
                {byMonth[index].length === 0 && <div className="text-[10px] text-[#999]">—</div>}
                {byMonth[index].map((e) => (
                  <Link key={e.calendar_id} to={`/calendar/${e.calendar_id}`} className={`block text-xs font-medium hover:text-[#C41E1E] ${e.status === "cancelled" ? "text-neutral-400 line-through" : ""}`}>
                    {e.skill_name}
                    <span className={`ml-1 inline-block w-1.5 h-1.5 rounded-full align-middle ${e.status === "conducted" ? "bg-green-500" : e.status === "revised" ? "bg-blue-500" : e.status === "cancelled" ? "bg-neutral-400" : "bg-amber-400"}`} />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* List */}
        <div className="bc-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F9F9F9] border-b border-[#E5E5E5]">
                <th className="text-left px-5 py-2.5 bc-label">Month</th>
                <th className="text-left px-5 py-2.5 bc-label">Competency</th>
                <th className="text-left px-5 py-2.5 bc-label">Source</th>
                <th className="text-left px-5 py-2.5 bc-label">Nominated</th>
                <th className="text-left px-5 py-2.5 bc-label">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr><td colSpan="6" className="px-5 py-8 text-center text-[#666666]">No calendar entries for {fyLabel(fy)}.</td></tr>
              )}
              {entries.map((e) => (
                <tr key={e.calendar_id} className="border-b border-[#E5E5E5] bc-row-hover">
                  <td className="px-5 py-2.5 font-mono">
                    {MONTHS[e.target_month - 1]}
                    {e.is_revised && <div className="text-[10px] text-blue-700">was {MONTHS[(e.original_month || 1) - 1]}</div>}
                  </td>
                  <td className="px-5 py-2.5 font-medium">{e.skill_name}</td>
                  <td className="px-5 py-2.5 text-xs">
                    <span
                      title={e.source === "TNI_driven"
                        ? "This training was planned based on identified employee skill gaps"
                        : "This training is required by management or regulatory requirement regardless of gaps"}
                      className="cursor-help"
                    >
                      {e.source === "TNI_driven" ? "TNI-driven" : "Mandatory"}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 font-mono">{e.nominated_participants.length}</td>
                  <td className="px-5 py-2.5"><StatusPill status={e.status} /></td>
                  <td className="px-5 py-2.5 text-right">
                    <Link to={`/calendar/${e.calendar_id}`} data-testid={`open-cal-${e.calendar_id}`} className="text-xs text-[#C41E1E] font-medium">Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50" onClick={() => setShowAdd(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={add} className="bg-white border border-[#E5E5E5] rounded-sm w-full max-w-md p-6 space-y-4" data-testid="add-entry-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Add calendar entry</h3>
              <button type="button" onClick={() => setShowAdd(false)}><X size={18} /></button>
            </div>
            <div>
              <label className="bc-label block mb-1">Competency</label>
              <select required data-testid="form-skill" value={form.skill_code} onChange={(e) => setForm({ ...form, skill_code: e.target.value })} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm">
                <option value="">Select…</option>
                {skills.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="bc-label block mb-1">Target month</label>
              <select data-testid="form-month" value={form.target_month} onChange={(e) => setForm({ ...form, target_month: e.target.value })} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm">
                {FY_MONTHS.map(([m]) => <option key={m} value={m}>{MONTHS[m - 1]}</option>)}
              </select>
            </div>
            <div>
              <label className="bc-label block mb-1">Source</label>
              <select data-testid="form-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm">
                <option value="TNI_driven">TNI-driven (gaps exist)</option>
                <option value="mandatory">Mandatory (regulatory)</option>
              </select>
            </div>
            <button type="submit" data-testid="form-submit" className="w-full bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm py-2.5 font-medium">Add to plan</button>
          </form>
        </div>
      )}

      {duplicateWarning && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-[60]" onClick={() => setDuplicateWarning(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-[#E5E5E5] rounded-sm w-full max-w-md"
            data-testid="calendar-duplicate-warning"
          >
            <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-start gap-3">
              <div className="w-9 h-9 bg-amber-50 text-amber-700 rounded-sm flex items-center justify-center shrink-0">
                <AlertTriangle size={18} />
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold">Duplicate calendar entry?</h3>
                <p className="text-sm text-[#1A1A1A] mt-2">
                  A training for <strong>{duplicateWarning.existing.skill_name}</strong> is already planned for{" "}
                  <strong>{MONTHS[duplicateWarning.existing.target_month - 1]} {duplicateWarning.existing.year}</strong>
                  {duplicateWarning.existing.status && duplicateWarning.existing.status !== "planned" && (
                    <> (currently <span className="font-mono">{duplicateWarning.existing.status}</span>)</>
                  )}
                  .
                </p>
                <p className="text-xs text-[#666666] mt-2">
                  Are you sure you want to create another entry? Doing so will leave both entries in the plan and the
                  override will be recorded in this entry&apos;s audit trail.
                </p>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-[#E5E5E5] flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDuplicateWarning(null)}
                disabled={creating}
                className="text-sm px-4 py-2 border border-[#E5E5E5] hover:border-[#0D0D0D] rounded-sm disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDuplicateCreate}
                disabled={creating}
                data-testid="duplicate-create-anyway"
                className="text-sm px-4 py-2 bg-[#C41E1E] hover:bg-[#A31818] text-white rounded-sm font-semibold disabled:opacity-60"
              >
                {creating ? "Creating…" : "Create Anyway"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
